// Mirrors backend-nest/src/onboarding/dto/generated-plan.zod.ts (GeneratedPlanSchema)
// and the OrgOnboardingState/OrganizationProfile Prisma shapes exposed by the
// 5 onboarding routes (design.md "Interfaces / Contracts").

export interface GeneratedPlanAgent {
  type: string;
  enabled: boolean;
  customInstructions?: string;
  rationale: string;
}

export interface GeneratedPlanSkill {
  name: string;
  enabled: boolean;
  rationale: string;
}

export interface GeneratedPlanVoiceAgent {
  useCase: string;
  name: string;
  successCriteria: string[];
}

export interface GeneratedPlanContext {
  industry: string;
  productSummary: string;
  salesMotion: string;
  targetAudience: string;
  tone: string;
}

export interface GeneratedPlan {
  outputLanguage?: OnboardingOutputLanguage;
  agents: GeneratedPlanAgent[];
  skills: GeneratedPlanSkill[];
  toolsPreview: string[];
  voiceAgent: GeneratedPlanVoiceAgent | null;
  context: GeneratedPlanContext;
  confidence: number;
}

export type OnboardingStatus = 'not_started' | 'in_progress' | 'completed' | 'skipped';

export type OnboardingMode = 'guided' | 'voice';

export interface OnboardingState {
  organizationId: string;
  status: OnboardingStatus;
  currentStep: number;
  generatedPlan: GeneratedPlan | null;
  appliedPlan?: GeneratedPlan | null;
  appliedAt?: string | null;
  dismissedAt?: string | null;
  completedAt?: string | null;
  onboardingMode?: OnboardingMode;
}

// Org-wide "general context" returned by GET /onboarding/profile. All fields
// are nullable; the backend returns this safe empty shape (never 404) when no
// profile exists yet. Agents without their own customization card fall back to
// these shared values.
export interface OrganizationProfile {
  organizationName?: string;
  industry: string | null;
  productSummary: string | null;
  salesMotion: string | null;
  targetAudience: string | null;
  targetMarketLocation?: string | null;
  tone: string | null;
  website: string | null;
  languages: string[];
}

export interface SaveProfileInput {
  organizationId: string;
  organizationName?: string;
  industry?: string;
  productSummary?: string;
  salesMotion?: string;
  targetAudience?: string;
  targetMarketLocation?: string;
  tone?: string;
  website?: string;
  languages?: string[];
  outputLanguage?: OnboardingOutputLanguage;
}

export type OnboardingOutputLanguage = 'en' | 'es' | 'pt';

export interface GeneratePlanInput {
  organizationId: string;
  /** Active UI language. Only selects the language of the RESPONSE — the plan
   *  is always generated and stored in English. */
  outputLanguage?: OnboardingOutputLanguage;
}

export type ProductKind = 'product' | 'service';

export type ProductTemplateLocale = 'es' | 'en' | 'pt';

export const DEFAULT_PRODUCT_KIND: ProductKind = 'product';

// One entry in the org's structured product/service catalog
// (OrganizationProduct). Separate from OrganizationProfile.productSummary,
// which stays as the free-text paragraph.
export interface OrganizationProduct {
  id: string;
  name: string;
  description: string | null;
  priceRange: string | null;
  isActive: boolean;
  position: number;
  kind?: ProductKind;
  aliases?: string[];
}

export interface CreateOrganizationProductInput {
  organizationId: string;
  name: string;
  description?: string;
  priceRange?: string;
  isActive?: boolean;
  kind?: ProductKind;
  aliases?: string[];
}

export interface UpdateOrganizationProductInput {
  organizationId: string;
  productId: string;
  name?: string;
  description?: string;
  priceRange?: string;
  isActive?: boolean;
  position?: number;
  kind?: ProductKind;
  aliases?: string[];
}

export type ProductImportRejectionReason =
  | 'missing_name'
  | 'name_too_long'
  | 'description_too_long'
  | 'price_range_too_long'
  | 'duplicate_in_file'
  | 'already_exists';

export interface ProductImportRejectedRow {
  row: number;
  reason: ProductImportRejectionReason;
}

export type ProductImportWarningType = 'generic_name' | 'aliases_truncated' | 'unknown_kind';

export interface ProductImportWarningRow {
  row: number;
  warning: ProductImportWarningType;
  name: string;
  suggestedName?: string;
}

export interface ProductImportResult {
  created: OrganizationProduct[];
  rejectedRows: ProductImportRejectedRow[];
  warnings?: ProductImportWarningRow[];
  totalRows: number;
}

export interface ImportOrganizationProductsInput {
  organizationId: string;
  file: FormData;
}

export interface DeleteOrganizationProductInput {
  organizationId: string;
  productId: string;
}

export type OnboardingStateAction = 'advance-step' | 'skip' | 'dismiss' | 'set-mode';

export interface PatchStateInput {
  organizationId: string;
  action: OnboardingStateAction;
  step?: number;
  onboardingMode?: OnboardingMode;
}

export interface ApplyPlanInput {
  organizationId: string;
  plan: GeneratedPlan;
  /** Language `plan` is written in, so the backend can tell edited text from
   *  untouched text when folding it back to English. */
  outputLanguage?: OnboardingOutputLanguage;
}

// Confidence fallback threshold (design.md "Confidence fallback: if
// confidence < 0.5 ... falls back to default enabledAgents"). Kept in sync
// with the backend's OnboardingInferenceService constant of the same value.
export const ONBOARDING_CONFIDENCE_FALLBACK_THRESHOLD = 0.5;
